// api.ts
import axios from "axios";

// Get the current hostname to determine if we're in production
const isProduction = window.location.hostname !== "localhost";

// Use consistent domain naming across frontend and backend for proper cookie sharing
// API host — default to custom sub-domain in prod, overrideable
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (isProduction ? "https://api.ivytrack.app" : "http://localhost:5000");

// Front-end URL – use the actual page origin so we always return
// to the domain the visitor is on (handles apex *and* www).
const FRONTEND_URL =
  process.env.REACT_APP_FRONTEND_URL || window.location.origin;

// ---------------------------------------------------------------------------
// Since authentication is now handled entirely through the secure session
// cookie set by the backend, we no longer pass or manage custom auth tokens
// via query parameters or request headers.  All previous token-related code
// has been removed.
// ---------------------------------------------------------------------------

// Configure axios defaults
axios.defaults.withCredentials = true;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 300000, // 5 minute timeout for transcript processing
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor (no token headers needed any more)
apiClient.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
);

// Custom event for auth errors
class AuthErrorEvent extends CustomEvent<{ error: string; endpoint: string }> {
  constructor(error: string, endpoint: string) {
    super("authError", { detail: { error, endpoint } });
  }
}

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 authentication errors
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.error || "";

      // Check if it's an authentication error
      if (
        errorMessage.includes("Authentication required") ||
        errorMessage.includes("Access restricted") ||
        !errorMessage
      ) {
        // Empty error message on 401 also indicates auth failure
        // Dispatch auth error event for UI components to react
        window.dispatchEvent(
          new AuthErrorEvent(
            "Authentication required",
            error.config?.url || "unknown"
          )
        );

        // Store current path to redirect back after sign-in
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== "/" && currentPath !== "/signin") {
          localStorage.setItem("redirectAfterAuth", currentPath);
        }

        // Clear any cached auth state and redirect to sign-in
        window.location.href = "/signin";
        return Promise.reject(
          new Error("Authentication required, redirecting to sign-in")
        );
      }
    }

    // Check for JWT expiration errors in 500 responses (legacy support)
    if (error.response?.status === 500) {
      const errorMessage = error.response?.data?.error || "";
      const isJWTExpired =
        errorMessage.includes("JWT expired") ||
        errorMessage.includes("Failed to create or retrieve user");

      if (isJWTExpired) {
        // Dispatch auth error event for UI components to react
        window.dispatchEvent(
          new AuthErrorEvent("Session expired", error.config?.url || "unknown")
        );

        // Store current path to redirect back after sign-in
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== "/" && currentPath !== "/signin") {
          localStorage.setItem("redirectAfterAuth", currentPath);
        }

        // Clear any cached auth state and redirect to sign-in
        window.location.href = "/signin";
        return Promise.reject(
          new Error("Session expired, redirecting to sign-in")
        );
      }
    }

    return Promise.reject(error);
  }
);

export const api = {
  // Helper to get base URL
  getBaseUrl: () => API_BASE_URL,
  // Return the origin of the current page so that the backend always
  // redirects to the exact domain the user is on (e.g. https://ivytrack.app
  // or https://www.ivytrack.app).  This prevents getting bounced between
  // sub-domains after Microsoft login.
  getFrontendUrl: () => window.location.origin,

  // Authentication methods
  getAuthStatus: async () => {
    const response = await apiClient.get("/api/auth/status");
    return response.data;
  },

  signOut: async () => {
    // Redirect browser through the backend sign-out endpoint; the backend
    // will clear the session and (optionally) log the user out of Microsoft
    // before returning to the frontend.
    window.location.href = `${API_BASE_URL}/api/auth/sign-out?next_link=${encodeURIComponent(
      FRONTEND_URL
    )}`;

    // Resolve immediately as the browser is navigating
    return Promise.resolve({ success: true });
  },

  // Generic HTTP methods for service layer use
  async get(endpoint: string) {
    return await apiClient.get(
      endpoint.startsWith("/") ? endpoint : `/${endpoint}`
    );
  },

  async post(endpoint: string, data: any) {
    return await apiClient.post(
      endpoint.startsWith("/") ? endpoint : `/${endpoint}`,
      data
    );
  },

  async put(endpoint: string, data: any) {
    return await apiClient.put(
      endpoint.startsWith("/") ? endpoint : `/${endpoint}`,
      data
    );
  },

  async delete(endpoint: string) {
    return await apiClient.delete(
      endpoint.startsWith("/") ? endpoint : `/${endpoint}`
    );
  },
};
